/**
 * What one person has done to a register: what they filtered it to, which columns they
 * kept, how they sorted it — and the named views they saved.
 *
 * Held per viewer in `localStorage`, keyed by register. Two reasons it is not on the server:
 *
 * - A view is a preference, not a record. Nothing governed depends on it, so it does not
 *   need an approval chain, an audit row, or a migration when someone adds a column.
 * - It is per person by nature. "Severe risks I own, newest first" is not a thing to share
 *   with a tenant; a shared view is a different feature with different rules, and building
 *   this one as if it were shared would make that harder rather than easier.
 *
 * Every read is wrapped: a private window, cleared site data, or a browser set to block
 * storage all throw rather than returning null, and a register that fails to render because
 * somebody has cookies off is a worse bug than a forgotten filter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

/** One condition, in the wire form the API parses: `column:operator:value`. */
export type Filter = {
  column: string
  operator: string
  value: string
}

export type SavedView = {
  id: string
  name: string
  filters: Filter[]
  hidden: string[]
  sort: string
}

export type GridView = {
  filters: Filter[]
  hidden: string[]
  sort: string
}

const VERSION = 1

function key(slug: string, what: 'view' | 'views'): string {
  // Versioned, so a future change to the shape can ignore old entries rather than crash on
  // them. An unreadable preference should cost a filter, never a screen.
  return `epmo.grid.v${VERSION}.${slug}.${what}`
}

function read<T>(name: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(name)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(name: string, value: unknown): void {
  try {
    window.localStorage.setItem(name, JSON.stringify(value))
  } catch {
    // Storage full, or blocked. The view still works for this session; it just will not be
    // remembered, which is the right thing to fail at.
  }
}

/** `column:operator:value`, or `column:operator` for the ones that take no value. */
export function toParam(filter: Filter): string {
  return filter.value === '' ? `${filter.column}:${filter.operator}` : `${filter.column}:${filter.operator}:${filter.value}`
}

export function useGridView(slug: string, defaultSort: string) {
  const [filters, setFilters] = useState<Filter[]>([])
  const [hidden, setHidden] = useState<string[]>([])
  const [sort, setSort] = useState(defaultSort)
  const [views, setViews] = useState<SavedView[]>([])
  const [activeView, setActiveView] = useState<string | null>(null)

  // Restore on mount and whenever the register changes. Reading in an effect rather than in
  // `useState`'s initialiser keeps the first server render and the first client render
  // identical, which is what stops a hydration mismatch.
  useEffect(() => {
    const stored = read<GridView | null>(key(slug, 'view'), null)
    setFilters(stored?.filters ?? [])
    setHidden(stored?.hidden ?? [])
    setSort(stored?.sort ?? defaultSort)
    setViews(read<SavedView[]>(key(slug, 'views'), []))
    setActiveView(null)
  }, [slug, defaultSort])

  // Remember the working state, so returning to a register returns to the question you were
  // asking of it.
  useEffect(() => {
    write(key(slug, 'view'), { filters, hidden, sort })
  }, [slug, filters, hidden, sort])

  const addFilter = useCallback((filter: Filter) => {
    setActiveView(null)
    setFilters((current) => {
      // One condition per column and operator. Adding `risk_score is at least 15` twice is
      // never intended, and two chips that say the same thing are impossible to tell apart.
      const without = current.filter(
        (existing) => !(existing.column === filter.column && existing.operator === filter.operator),
      )
      return [...without, filter]
    })
  }, [])

  const removeFilter = useCallback((index: number) => {
    setActiveView(null)
    setFilters((current) => current.filter((_, at) => at !== index))
  }, [])

  const clearFilters = useCallback(() => {
    setActiveView(null)
    setFilters([])
  }, [])

  const toggleColumn = useCallback((name: string) => {
    setActiveView(null)
    setHidden((current) =>
      current.includes(name) ? current.filter((other) => other !== name) : [...current, name],
    )
  }, [])

  const showAllColumns = useCallback(() => {
    setActiveView(null)
    setHidden([])
  }, [])

  const saveView = useCallback(
    (name: string) => {
      const view: SavedView = {
        id: `${Date.now()}`,
        name: name.trim(),
        filters,
        hidden,
        sort,
      }
      setViews((current) => {
        // Saving over a name replaces it. Two views called "Severe" is a menu nobody can use.
        const next = [...current.filter((other) => other.name !== view.name), view]
        write(key(slug, 'views'), next)
        return next
      })
      setActiveView(view.id)
    },
    [slug, filters, hidden, sort],
  )

  const applyView = useCallback((view: SavedView) => {
    setFilters(view.filters)
    setHidden(view.hidden)
    setSort(view.sort)
    setActiveView(view.id)
  }, [])

  const deleteView = useCallback(
    (id: string) => {
      setViews((current) => {
        const next = current.filter((view) => view.id !== id)
        write(key(slug, 'views'), next)
        return next
      })
      setActiveView((current) => (current === id ? null : current))
    },
    [slug],
  )

  /** The `filter=` parameters this view sends. Memoised because it keys the row query. */
  const params = useMemo(() => filters.map(toParam), [filters])

  return {
    filters,
    hidden,
    sort,
    views,
    activeView,
    params,
    setSort: (next: string) => {
      setActiveView(null)
      setSort(next)
    },
    addFilter,
    removeFilter,
    clearFilters,
    toggleColumn,
    showAllColumns,
    saveView,
    applyView,
    deleteView,
  }
}
